import React, { useState, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { IconSearch } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useExchangeStore } from '@app/hooks/trade/exchangeStore';
import { useTickerStore } from '@app/hooks/ticketStore';
import { useCopyPortfolioStore } from '@app/hooks/copyTrading/copyPortfolioStore';
import { VITE_APP_API_MEDIA_URL } from '@/app/constants/APIKEYS';
import { useTranslation } from 'react-i18next';

const arrowIconUrl = '/assets/newHome/menu/arrow-icon.svg';

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  height: 36px;
  padding: 4px 12px;
  border: 1px solid #fff;
  border-radius: 12px;
  background: rgba(0,0,0,0.2);
  width: 188px;
  position: relative;
  cursor: pointer;
`;

const SearchInput = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: #fff;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 12px;
  letter-spacing: -0.24px;
  cursor: pointer;

  &::placeholder {
    color: #b3b5bd;
  }
`;

const SearchDropdown = styled.div<{ $isOpen: boolean }>`
  display: ${(props) => (props.$isOpen ? 'block' : 'none')};
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  width: 352px;
  background: #0d1117;
  border: 1px solid #353535;
  border-radius: 12px;
  padding: 0;
  max-height: 500px;
  overflow-y: auto;
  z-index: 1000;
`;

const TopSearchedSection = styled.div`
  padding: 16px;
  padding-bottom: 0;
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #353535;
`;

const SectionTitleText = styled.p`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: -0.28px;
  margin: 0;
`;

const CoinList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-left: 3px;
`;

const CoinItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;

const CoinInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CoinHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const CoinIcon = styled.img`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`;

const CoinSymbol = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 2px;
`;

const CoinSymbolText = styled.p`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  color: #ffffff;
  letter-spacing: -0.84px;
  margin: 0;
  height: 15px;
`;

const CoinQuote = styled.p`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 12px;
  color: #b3b5bd;
  letter-spacing: -0.24px;
  margin: 0;
`;

const CoinType = styled.p`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 12px;
  color: #777986;
  letter-spacing: -0.24px;
  margin: 0;
`;

const CoinPrice = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
`;

const PriceValue = styled.p`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: -0.84px;
  margin: 0;
  white-space: pre-wrap;
`;

const PriceChange = styled.p<{ $isPositive: boolean }>`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: ${(props) => (props.$isPositive ? '#4caf50' : '#f44336')};
  letter-spacing: -0.72px;
  margin: 0;
  text-align: right;
  white-space: pre-wrap;
`;

const MasterTradersSection = styled.div`
  padding: 0 19px 16px;
  margin: 32px 16px 0;
  padding-top: 24px;
  border-top: 1px solid #353535;
`;

const MasterTradersHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const MasterTradersTitle = styled.p`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: -0.28px;
  margin: 0;
`;

const ArrowDownIcon = styled.img`
  width: 16px;
  height: 16px;
  transform: rotate(270deg);
  display: block;
`;

const TraderList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TraderCard = styled.div`
  background: #0c2323;
  border-radius: 16px;
  padding: 12px 16px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const TraderInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const TraderAvatar = styled.div`
  width: 36px;
  height: 36px;
  background: #ffffff;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const TraderAvatarImage = styled.img`
  width: 24px;
  height: 20px;
`;

const TraderDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TraderName = styled.p`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  color: #ffffff;
  letter-spacing: -0.28px;
  margin: 0;
`;

const TraderFollowers = styled.p`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 12px;
  color: #9496a1;
  letter-spacing: -0.24px;
  margin: 0;
`;

const TraderPnL = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
`;

const TraderPnLLabel = styled.p`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 10px;
  color: #b3b5bd;
  letter-spacing: -0.6px;
  margin: 0;
  white-space: pre-wrap;
`;

const TraderPnLValue = styled.p`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: -0.72px;
  margin: 0;
`;

const SearchBar: React.FC = () => {
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const { EPs } = useExchangeStore();
  const { connect, data: tickerData, closeWS } = useTickerStore();
  const { portfolios, fetchPortfolios } = useCopyPortfolioStore();
    const { t, i18n } = useTranslation();

  // 외부 클릭 시 검색창 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        isSearchOpen &&
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setIsSearchOpen(false);
        setSearchValue('');
      }
    };

    if (isSearchOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isSearchOpen]);

  // 티커 데이터 연결
  useEffect(() => {
    if (isSearchOpen && EPs && EPs.length > 0) {
      const symbols = EPs.map((ep: any) =>
        ep.pairs.replace('/', '').toLowerCase()
      );
      connect(symbols);
    }
    return () => {
      if (isSearchOpen) {
        closeWS();
      }
    };
  }, [isSearchOpen, EPs, connect, closeWS]);

  // 코인 데이터 포맷팅
  const formatPrice = (price: string): string => {
    const num = parseFloat(price);
    if (num >= 1000) {
      return num.toLocaleString('en-US', { maximumFractionDigits: 2 });
    } else if (num >= 1) {
      return num.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    } else {
      return num.toFixed(8).replace(/\.?0+$/, '');
    }
  };

  // 검색 필터링된 코인 목록 (기존 코드와 동일한 방식)
  const filteredCoins = useMemo(() => {
    if (!EPs || !searchValue) {
      return [];
    }

    const filtered = EPs.filter((item: any) =>
      item?.symbol?.toLowerCase()?.includes(searchValue?.toLowerCase())
    );

    return filtered.slice(0, 5).map((ep: any) => {
      const symbolKey = ep.pairs.replace('/', '').toLowerCase();
      const ticker = tickerData[symbolKey];

      return {
        symbol:
          ep.symbol?.replace('/', '').replace('USDT', '') ||
          ep.symbol?.split('/')[0] ||
          '',
        quote: ep.symbol?.split('/')[1] || 'USDT',
        type: 'USDT Perpetual',
        price: ticker ? formatPrice(ticker.c || '0') : '0',
        change: ticker ? parseFloat(ticker.P || '0') : 0,
        icon: ep.image || '',
        epId: ep.id,
      };
    });
  }, [EPs, searchValue, tickerData]);

  // Top Searched 코인 목록 (거래량 기준 상위 6개) - 검색어가 없을 때만 표시
  const topSearchedCoins = useMemo(() => {
    if (
      searchValue ||
      !EPs ||
      !tickerData ||
      Object.keys(tickerData).length === 0
    ) {
      return [];
    }

    const tickerArray = Object.entries(tickerData)
      .map(([key, data]: [string, any]) => ({
        symbol: key,
        ticker: data,
        ep: EPs.find(
          (ep: any) =>
            ep.pairs.replace('/', '').toLowerCase() === key.toLowerCase()
        ),
      }))
      .filter((item) => item.ep);

    return tickerArray
      .sort(
        (a, b) => parseFloat(b.ticker.q || '0') - parseFloat(a.ticker.q || '0')
      )
      .slice(0, 6)
      .map((item, index) => ({
        symbol:
          item.ticker.s?.replace('USDT', '') ||
          item.symbol.replace('usdt', '').toUpperCase(),
        quote: 'USDT',
        type: 'USDT Perpetual',
        price: formatPrice(item.ticker.c || '0'),
        change: parseFloat(item.ticker.P || '0'),
        icon: item.ep?.image || '',
        epId: item.ep?.id,
      }));
  }, [EPs, tickerData, searchValue]);

  // Top 3 Master Traders - 기존 코드와 동일한 API 사용
  useEffect(() => {
    if (isSearchOpen) {
      fetchPortfolios({
        sortBy: 'monthlyRoi' as any,
        order: 'desc',
        status: 1,
        page: 1,
        pageSize: 3,
      });
    }
  }, [isSearchOpen, fetchPortfolios]);

  const topTraders = useMemo(() => {
    if (!portfolios || portfolios.length === 0) {
      return [];
    }
    return portfolios.slice(0, 3).map((portfolio) => {
      const pnl = portfolio.totalPnl || 0;
      const pnlFormatted =
        pnl >= 0
          ? `+ ${pnl.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
          : `${pnl.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

      return {
        name: portfolio.leaderUsername || 'Unknown',
        followers: portfolio.currentFollowers || 0,
        totalPnL: pnlFormatted,
        avatar: portfolio.leaderProfileImage || portfolio.leaderAvatar || '',
      };
    });
  }, [portfolios]);

  const handleSearchBoxClick = () => {
    setIsSearchOpen(!isSearchOpen);
  };

  const handleCoinClick = (epId: number) => {
    navigate(`/trade/${epId}`);
    setIsSearchOpen(false);
  };

  return (
    <SearchContainer ref={searchRef} onClick={handleSearchBoxClick}>
      <IconSearch size={20} color="#fff" />
      <SearchInput
        type="text"
        placeholder={t("Find Token")}
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        onClick={(e) => {
          e.stopPropagation();
          setIsSearchOpen(true);
        }}
      />
      <SearchDropdown $isOpen={isSearchOpen}>
        {searchValue ? (
          // 검색 결과 표시 (기존 코드와 동일한 방식)
          <TopSearchedSection>
            <SectionTitle>
              <SectionTitleText>Search</SectionTitleText>
            </SectionTitle>
            <CoinList>
              {filteredCoins.length > 0 ? (
                filteredCoins.map((coin, index) => (
                  <CoinItem
                    key={index}
                    onClick={() => coin.epId && handleCoinClick(coin.epId)}
                  >
                    <CoinInfo>
                      <CoinHeader>
                        {coin.icon && (
                          <CoinIcon
                            src={
                              coin.icon.startsWith('http')
                                ? coin.icon
                                : `${VITE_APP_API_MEDIA_URL}/${coin.icon}`
                            }
                            alt={coin.symbol}
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        )}
                        <CoinSymbol>
                          <CoinSymbolText>{coin.symbol}</CoinSymbolText>
                          <CoinQuote>{coin.quote}</CoinQuote>
                        </CoinSymbol>
                      </CoinHeader>
                      <CoinType>{coin.type}</CoinType>
                    </CoinInfo>
                    <CoinPrice>
                      <PriceValue>{coin.price}</PriceValue>
                      <PriceChange $isPositive={coin.change >= 0}>
                        {coin.change >= 0 ? '+' : ''}
                        {coin.change.toFixed(2)}%
                      </PriceChange>
                    </CoinPrice>
                  </CoinItem>
                ))
              ) : (
                <div
                  style={{
                    color: '#b3b5bd',
                    textAlign: 'center',
                    padding: '20px',
                  }}
                >
                  No results found
                </div>
              )}
            </CoinList>
          </TopSearchedSection>
        ) : (
          // 검색어가 없을 때 Top Searched 표시
          <TopSearchedSection>
            <SectionTitle>
              <SectionTitleText>Top Searched</SectionTitleText>
            </SectionTitle>
            <CoinList>
              {topSearchedCoins.length > 0 ? (
                topSearchedCoins.map((coin, index) => (
                  <CoinItem
                    key={index}
                    onClick={() => coin.epId && handleCoinClick(coin.epId)}
                  >
                    <CoinInfo>
                      <CoinHeader>
                        {coin.icon && (
                          <CoinIcon
                            src={
                              coin.icon.startsWith('http')
                                ? coin.icon
                                : `${VITE_APP_API_MEDIA_URL}/${coin.icon}`
                            }
                            alt={coin.symbol}
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        )}
                        <CoinSymbol>
                          <CoinSymbolText>{coin.symbol}</CoinSymbolText>
                          <CoinQuote>{coin.quote}</CoinQuote>
                        </CoinSymbol>
                      </CoinHeader>
                      <CoinType>{coin.type}</CoinType>
                    </CoinInfo>
                    <CoinPrice>
                      <PriceValue>{coin.price}</PriceValue>
                      <PriceChange $isPositive={coin.change >= 0}>
                        {coin.change >= 0 ? '+' : ''}
                        {coin.change.toFixed(2)}%
                      </PriceChange>
                    </CoinPrice>
                  </CoinItem>
                ))
              ) : (
                <div
                  style={{
                    color: '#b3b5bd',
                    textAlign: 'center',
                    padding: '20px',
                  }}
                >
                  Loading...
                </div>
              )}
            </CoinList>
          </TopSearchedSection>
        )}
        <MasterTradersSection>
          <MasterTradersHeader>
            <MasterTradersTitle>Top 3 Master Traders</MasterTradersTitle>
            <ArrowDownIcon src={arrowIconUrl} alt="Arrow" />
          </MasterTradersHeader>
          <TraderList>
            {topTraders.length > 0 ? (
              topTraders.map((trader, index) => (
                <TraderCard key={index}>
                  <TraderInfo>
                    <TraderAvatar>
                      {trader.avatar ? (
                        <TraderAvatarImage
                          src={
                            trader.avatar.startsWith('http')
                              ? trader.avatar
                              : `${VITE_APP_API_MEDIA_URL}/${trader.avatar}`
                          }
                          alt={trader.name}
                          onError={(e) => {
                            e.currentTarget.src =
                              '/assets/newHome/menu/wallet-icon.svg';
                          }}
                        />
                      ) : (
                        <TraderAvatarImage
                          src="/assets/newHome/menu/wallet-icon.svg"
                          alt={trader.name}
                        />
                      )}
                    </TraderAvatar>
                    <TraderDetails>
                      <TraderName>{trader.name}</TraderName>
                      <TraderFollowers>
                        {trader.followers.toLocaleString()} Followers
                      </TraderFollowers>
                    </TraderDetails>
                  </TraderInfo>
                  <TraderPnL>
                    <TraderPnLLabel>Total PnL</TraderPnLLabel>
                    <TraderPnLValue>{trader.totalPnL}</TraderPnLValue>
                  </TraderPnL>
                </TraderCard>
              ))
            ) : (
              <div
                style={{
                  color: '#b3b5bd',
                  textAlign: 'center',
                  padding: '20px',
                }}
              >
                Loading...
              </div>
            )}
          </TraderList>
        </MasterTradersSection>
      </SearchDropdown>
    </SearchContainer>
  );
};

export default SearchBar;
